import { Icon } from "@tutor/ui";
import { useState } from "react";
import type * as api from "./api";
import { resumeSession } from "./api";
import { errorKey } from "./errors";
import { useParentT, type ParentStringKey } from "./strings";

// A high safety flag pauses Coach and practice until a parent lifts it or the UTC day ends
// (ADR-0022). While the selected child may be paused, this banner sits above every parent
// tab with the safety team's advice and the lift button, so a parent never has to hunt for
// it in the alerts list. Lifting needs a fresh email code (handled by `onAuthError`).

export function PauseBanner(props: {
  profileId: string;
  alerts: readonly api.ParentAlertRow[];
  onAuthError: (error: api.ApiError) => boolean;
}) {
  const { profileId, alerts, onAuthError } = props;
  const t = useParentT();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Readonly<Record<string, ParentStringKey>>>({});
  if (!alerts.some((a) => a.profileId === profileId && pausedToday(a))) return null;
  const done = outcome[profileId];

  const resume = async () => {
    setBusy(true);
    const res = await resumeSession(profileId);
    setBusy(false);
    if (res.ok) {
      const key = res.value.resumed ? "alerts.resumed" : "alerts.notPaused";
      setOutcome((was) => ({ ...was, [profileId]: key }));
      return;
    }
    if (!onAuthError(res.error))
      setOutcome((was) => ({ ...was, [profileId]: errorKey(res.error) }));
  };

  if (done === "alerts.resumed" || done === "alerts.notPaused") {
    return (
      <p className="pm-pause pm-pause--done" role="status">
        <Icon name="check" size={20} />
        {t(done)}
      </p>
    );
  }
  return (
    <section className="pm-pause" role="region" aria-label={t("alerts.resume")}>
      <span className="pm-pause__icon">
        <Icon name="alert" size={20} />
      </span>
      <div className="pm-pause__body">
        <p className="pm-pause__text">{t("alerts.pauseExplain")}</p>
        {done === undefined ? null : (
          <p className="parent-message" role="alert">
            {t(done)}
          </p>
        )}
        <button
          type="button"
          className="parent-button parent-button-primary"
          disabled={busy}
          onClick={() => void resume()}
        >
          {t("alerts.resume")}
        </button>
      </div>
    </section>
  );
}

/** A high safety alert from today's UTC day: the pause it started may still hold. */
export function pausedToday(alert: api.ParentAlertRow, now: Date = new Date()): boolean {
  return (
    alert.reason === "safety_high" &&
    Math.floor(Date.parse(alert.createdAt) / 86_400_000) === Math.floor(now.getTime() / 86_400_000)
  );
}
